import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/ErrorState';
import { SectionCard } from '@/components/common/SectionCard';
import { useDataRoomAccess } from '@/hooks/useDataRoomAccess';
import { ROUTES } from '@/lib/constants/routes';
import { DATA_ROOM_STRINGS } from '../constants';

const COPY = DATA_ROOM_STRINGS.PAGE;

export interface DataRoomSeatsCardProps {
  /** The acting startup entity. */
  startupId: string;
}

/**
 * Who holds the startup's data-room seats, on the Data Room page.
 * Rendered for a caller holding `billing.manage` only, because the access
 * view it reads is refused to anyone else. Seats are changed on Team Access,
 * so a two-seat service links there; an Issue Report's single seat cannot
 * move (FR-138), so it does not.
 */
export function DataRoomSeatsCard({ startupId }: DataRoomSeatsCardProps) {
  const access = useDataRoomAccess(startupId);

  if (access.isError) {
    return <ErrorState title={COPY.SEATS_TITLE} onRetry={() => void access.refetch()} />;
  }
  const current = access.data?.current;
  if (!current) return null;

  const used = current.seats.filter((seat) => seat.holder).length;
  return (
    <SectionCard title={COPY.SEATS_TITLE}>
      <p className="text-text-primary text-sm font-semibold">
        {COPY.SEATS_USED.replace('{used}', String(used)).replace('{limit}', String(current.seatLimit))}
      </p>
      <ul className="m-0 mt-3 flex list-none flex-col gap-2 p-0">
        {current.seats.map((seat) => (
          <li key={seat.slot} className="text-text-secondary text-sm">
            {seat.holder?.name ?? COPY.SEAT_EMPTY}
          </li>
        ))}
      </ul>
      {current.seatLimit > 1 && (
        <Button asChild variant="link" className="mt-5 block w-fit">
          <Link to={ROUTES.STARTUP_TEAM}>{COPY.MANAGE_SEATS}</Link>
        </Button>
      )}
    </SectionCard>
  );
}
